import { get as getProp } from 'dot-prop';
import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  BaseObjectConfig,
  Design,
  DesignItemType,
  DesignModuleNodeProps
} from '@app/types';
import {
  filterNull,
  refreshCanvasItem,
  syncObjectToSerialized
} from '@app/utilities';
import { useDesign } from './use-design';
import {
  UseValueControlProps,
  useValueForm,
  UseValueFormErrors,
  UseValueFormProps,
  UseValueFormResult
} from './useValueForm';

const calculateSavedValues = <TConfig extends BaseObjectConfig>(
  object: UseObjectFormObject<TConfig>
): UseObjectFormValues<TConfig> => {
  return {
    name: object.name,
    ...object.props.config
  };
};

const isDuplicateObjectName = (
  serialized: Design,
  newName: string,
  object: UseObjectFormObject<BaseObjectConfig>
): boolean => {
  return serialized.components.objects.some((o) => {
    return (
      o.id !== (object.props.parentComponentID ?? object.id)
      && o.props.nodeTypeName === object.props.nodeTypeName
      && o.name === newName
    );
  });
};

const transformObjectName = (value: string): string => {
  // Everything except for trailing underscores are handled onChange on the input.
  return value.replace(/_+$/g, '');
};

export type UseObjectFormObject<TConfig extends BaseObjectConfig> = {
  id: string;
  name: string;
  props: DesignModuleNodeProps<TConfig>;
  type: DesignItemType;
};

export type UseObjectFormValues<TConfig extends BaseObjectConfig> = (
  { name: string; }
  // The `name` property on @see TConfig cannot be set because we set name at the base of the object instead.
  & Omit<TConfig, 'name'>
);

export type UseObjectFormProps<TConfig extends BaseObjectConfig> = (
  Pick<
    UseValueFormProps<UseObjectFormValues<TConfig>>,
    'transformValues' | 'validate'
  >
  & { object: UseObjectFormObject<TConfig>; }
);

export type UseObjectFormGetControlPropsConfig = {
  syncOn?: 'blur' | 'change';
};

export type UseObjectFormResult<TConfig extends BaseObjectConfig> = (
  Omit<UseValueFormResult<UseObjectFormValues<TConfig>>, 'getControlProps'>
  & {
    getControlProps: <K extends keyof UseObjectFormValues<TConfig> & string>(
      key: K,
      config?: UseObjectFormGetControlPropsConfig
    ) => UseValueControlProps<UseObjectFormValues<TConfig>[K]>;
  }
);

export const useObjectForm = <TConfig extends BaseObjectConfig>({
  object,
  transformValues: transformValuesProp,
  validate: validateProp
}: UseObjectFormProps<TConfig>): UseObjectFormResult<TConfig> => {
  type TFormValues = UseObjectFormValues<TConfig>;
  type FormKey = keyof(TFormValues);

  const serialized = useDesign();
  const [savedValues, setSavedValues] = useState((): TFormValues => calculateSavedValues(object));
  const objectRef = useRef(object);
  objectRef.current = object;

  const transformValues = useCallback((values: TFormValues) => {
    const name = transformObjectName(values.name);
    const newValues = { ...values, name };
    return (transformValuesProp) ? transformValuesProp(newValues) : newValues;
  }, [transformValuesProp]);

  const validate = useCallback((values: TFormValues) => {
    let errors: UseValueFormErrors<TFormValues> = {};
    const transformedName = transformObjectName(values.name);

    if (!transformedName) {
      errors.name = 'Name is required.';
    } else if (isDuplicateObjectName(serialized, transformedName, object)) {
      errors.name = 'A duplicate name exists in the design.';
    }

    errors = filterNull({
      ...errors,
      ...validateProp?.(values)
    }) as UseValueFormErrors<TFormValues>;

    if (Object.keys(errors).length) {
      // We can do this since filterNull was called above.
      object.props.error = errors as Record<string, string>;
    } else {
      delete object.props.error;
    }
    return errors;
  }, [object, serialized, validateProp]);

  const form = useValueForm<TFormValues>({
    savedValues,
    transformValues,
    validate,
    validateControlOnChange: true,
    validateOnInit: true
  });

  /** Syncs transformed values to the provided @see object. */
  const handleSync = useCallback((newValues: TFormValues) => {
    const object = objectRef.current;
    const changes = Object.entries(newValues).map(([
      key,
      newValue
    ]): [string, unknown] => {
      return [
        (key === 'name') ? key : `props.config.${key}`,
        newValue
      ];
    }).filter(([key, newValue]) => {
      const oldValue = getProp(object, key);
      return (
        // Value changed.
        JSON.stringify(oldValue) !== JSON.stringify(newValue)
        // New name is not a duplicate.
        && (
          key !== 'name'
          || !isDuplicateObjectName(serialized, newValue as string, object)
        )
      );
    });
    changes.push(['props.error', object.props.error]);

    syncObjectToSerialized(serialized, object, changes);
    setSavedValues(calculateSavedValues(object));
  }, [serialized]);

  // Sync to object when inputs blur on @see PropertyPanelWidget.
  const getControlProps = <K extends FormKey & string>(
    key: K,
    config?: UseObjectFormGetControlPropsConfig
  ): UseValueControlProps<TFormValues[K]> => {
    const baseProps = form.getControlProps(key);
    const controlProps = { ...baseProps };
    const { syncOn = 'blur' } = config ?? {};

    if (syncOn === 'change') {
      controlProps.onChange = (...args) => {
        baseProps.onChange(...args);
        // Wait for the form to have the new value.
        setTimeout(() => {
          handleSync(form.getValues());
        });
      };
    } else {
      controlProps.onBlur = (...args) => {
        baseProps.onBlur?.(...args);
        handleSync(form.getValues());
      };
    }

    return controlProps;
  };

  // Update saved values if passed in object changes (this never happens in current node models).
  useEffect(() => {
    setSavedValues(calculateSavedValues(object));
  }, [object]);

  // Refresh canvas to hide/show errors.
  useEffect(() => {
    // The form's validation adds the errors to the object too.
    refreshCanvasItem(object);
  }, [form.errors, object]);

  // Sync to object when @see PropertyPanelWidget closes.
  useEffect(() => {
    // Internally it reads formValues.current (a ref), so it always returns the latest values.
    return () => handleSync(form.getValues());
  }, []);

  return {
    ...form,
    getControlProps,
    // Sync to object when form submits.
    createSubmitHandler: (handleSubmit) => {
      return form.createSubmitHandler((values, event) => {
        handleSync(values);
        handleSubmit?.(values, event);
      });
    }
  };
};
